import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Star,
  Film,
  Handshake,
  ArrowRight,
  ExternalLink,
  Sparkles,
  Gift,
  Calendar,
  ShieldCheck,
  Video,
  ClipboardList,
  Megaphone,
  Layers,
  Scissors,
  Wrench,
  Camera,
  PenLine,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MainContent } from "@/components/layout/MainContent";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { MODULE_ONE_LINERS, WEDGE_COPY } from "@/lib/wedge";
import { MAIN_SITE_LINKS } from "@/lib/domainConfig";
import useExternalAnalytics from "@/hooks/useExternalAnalytics";
import TrainingBoxesSection from "@/components/hub/TrainingBoxesSection";
import EcosystemConnectionDiagram from "@/components/hub/EcosystemConnectionDiagram";
import ReadinessSnapshot from "@/components/hub/ReadinessSnapshot";
import PersonaRouter from "@/components/hub/PersonaRouter";
import OutcomeStrip from "@/components/hub/OutcomeStrip";
import MoatsStrip from "@/components/hub/MoatsStrip";
import {
  captureOutreachFromUrl,
  OUTREACH_TO_PLATFORM,
  type OutreachKey,
} from "@/lib/outreachContext";

/**
 * Logged-out product launcher for hub.castready.org.
 *
 * Marketing content (personas, pricing pitch, founder pilot, investor
 * traction, testimonials, advisor strip, etc.) lives on castready.org.
 * This page exists to do exactly one job: get the visitor into the
 * right product, or into sign-up / sign-in for it.
 */
/**
 * Platform cards are role-led (item #3 of the launch audit) — the
 * person identifies themselves first, the product name reads as
 * subtitle. Each card lists the three concrete next steps the user
 * will see after signup (item #6) and states the trial / sales-led
 * offer explicitly (items #9 + #10).
 */
const PLATFORMS = [
  {
    key: "coach",
    role: "I coach actors or run a studio",
    title: "CastReady Coach",
    audience: "Coaches, studios & talent reps",
    oneLiner: MODULE_ONE_LINERS.coach,
    subtext: "Talent reps & management: manage roster readiness alongside coaches.",
    icon: GraduationCap,
    loginHref: "/login/coach",
    signupHref: "/signup/coach/plan",
    offer: "Founding Pilot · sales-led",
    afterSignup: [
      "Import your roster",
      "Assign sides & lessons",
      "See Readiness across every student",
    ],
    primaryCta: { label: "Book a pilot call", href: "/book-demo" },
    secondaryCta: { label: "Sign in", href: "/login/coach" },
    featured: true,
  },
  {
    key: "callback-zone",
    role: "I'm an actor or parent of one",
    title: "Callback Zone",
    audience: "Actors & families",
    oneLiner: MODULE_ONE_LINERS.callback_zone,
    subtext: "Parents: track your child's practice, tapes, and callback readiness.",
    icon: Star,
    loginHref: "/login/callback-zone",
    signupHref: "/signup/callback-zone/plan",
    offer: "7-day free trial · cancel anytime",
    afterSignup: [
      "Pick your age / age of your child",
      "Get today's practice",
      "Upload your first tape for review",
    ],
    primaryCta: { label: "Start free trial", href: "/signup/callback-zone/plan" },
    secondaryCta: { label: "Sign in", href: "/login/callback-zone" },
  },
  {
    key: "castings",
    role: "I cast roles or submit talent",
    title: "CastReady Castings",
    audience: "Casting pros, agents & managers",
    oneLiner: MODULE_ONE_LINERS.castings,
    subtext: "Submissions backed by training history — not just a headshot.",
    icon: Film,
    loginHref: "/login/castings",
    signupHref: "/signup/castings/plan",
    offer: "Sales-led · talk to our team",
    afterSignup: [
      "Post a breakdown",
      "Receive tapes with Readiness signal",
      "Shortlist & message faster",
    ],
    primaryCta: { label: "Talk to sales", href: "/book-demo" },
    secondaryCta: { label: "Sign in", href: "/login/castings" },
  },
  {
    key: "pro",
    role: "I'm a brand or partner",
    title: "CastReady Pro",
    audience: "Co-branded boxes & marketing exposure (invite only)",
    oneLiner: MODULE_ONE_LINERS.pro,
    subtext: "Collaborate on co-branded Training Kits and run paid exposure across the CastReady ecosystem.",
    icon: Handshake,
    loginHref: "/login/pro",
    signupHref: "/pro/apply",
    offer: "Invite only · partnership-led",
    afterSignup: [
      "Configure a co-branded box",
      "Buy marketplace / spotlight exposure",
      "Track rev share, affiliates & payouts",
    ],
    primaryCta: { label: "Apply", href: "/pro/apply" },
    secondaryCta: { label: "Sign in", href: "/login/pro" },
  },
] as const;

const EXPANSION_MODULES = [
  { key: "draftline", label: "Draftline", icon: PenLine,  blurb: "Write & share industry-format scripts." },
  { key: "capture",   label: "Capture",   icon: Camera,   blurb: "Photographer & videographer bookings." },
  { key: "hmu",       label: "HMU",       icon: Scissors, blurb: "Vetted hair, makeup & wardrobe pros." },
  { key: "crew",      label: "Crew",      icon: Wrench,   blurb: "Discover youth-safety-certified crew." },
] as const;

const HubLauncher = () => {
  const { trackBoth } = useExternalAnalytics();
  const [outreach, setOutreach] = useState<OutreachKey | null>(null);

  // Capture ?ref=studio|coach|actor|parent|partner|casting once on mount.
  useEffect(() => {
    const ref = captureOutreachFromUrl();
    if (ref) {
      setOutreach(ref);
      trackBoth("hub_outreach_landing", { ref });
    }
  }, [trackBoth]);

  const highlightedPlatform = useMemo(
    () => (outreach ? OUTREACH_TO_PLATFORM[outreach] : null),
    [outreach],
  );

  const onCardClick = (platform: string, kind: "login" | "signup" | "primary" | "secondary") => {
    trackBoth("hub_launcher_click", { platform, kind, outreach: outreach ?? null });
  };

  return (
    <>
      <PageMeta
        title="CastReady · The Actor Development Operating System"
        description="One workspace for training, practice, readiness, and opportunity — built for actors and the coaches who shape them."
      />
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-background via-background to-primary/[0.03]">
        <Header />
        <MainContent className="flex-1">
          <section className="relative pt-28 pb-20 overflow-hidden">
            {/* Ambient gradient blobs */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 -top-24 h-[420px] bg-[radial-gradient(60%_60%_at_50%_0%,hsl(var(--primary)/0.12),transparent_70%)]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -left-32 top-40 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -right-32 top-60 h-72 w-72 rounded-full bg-accent/20 blur-3xl"
            />

            <div className="container relative mx-auto px-6 max-w-5xl">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 backdrop-blur px-3 py-1 text-xs text-muted-foreground mb-5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  {WEDGE_COPY.positioning}
                </div>
                <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
                  From First Lesson to{" "}
                  <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                    Final Cast
                  </span>
                </h1>
                <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                  One workspace for training, practice, readiness, and opportunity — built for actors and the coaches who shape them.
                </p>
                <p className="mt-3 text-sm text-muted-foreground max-w-2xl mx-auto">
                  Most studios run on <span className="font-medium text-foreground">5–12 disconnected tools</span>.
                  CastReady replaces the patchwork with one connected ecosystem.{" "}
                  <a
                    href={MAIN_SITE_LINKS.home + "/how-it-works"}
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    What is CastReady?
                  </a>
                </p>
              </div>

              {/* Item #5 — persona self-identification chips */}
              <PersonaRouter />

              {/* Item #14 — outcome / differentiator strip */}
              <OutcomeStrip />

              {/* Item #1 — one-screen "how the ecosystem connects" diagram */}
              <EcosystemConnectionDiagram />

              {/* Item #4 — Readiness made visible, not described */}
              <ReadinessSnapshot />

              {/* Studio / agency pilot rail — wedge entry point for B2B. */}
              <div className="mb-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] via-card/60 to-card/40 backdrop-blur p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground">
                      Running a studio, agency, or management roster?
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Coach is our current go-to-market — book a 20-minute pilot
                      call to see roster, assignments, and readiness in one place.
                    </div>
                  </div>
                </div>
                <Button asChild size="sm" className="gap-1.5 shrink-0">
                  <Link
                    to="/book-demo"
                    onClick={() => trackBoth("hub_launcher_click", { platform: "coach", kind: "pilot_call" })}
                  >
                    <Calendar className="h-4 w-4" />
                    Book a pilot call
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PLATFORMS.map((p) => {
                  const Icon = p.icon;
                  const featured = "featured" in p && p.featured;
                  const isHighlighted = highlightedPlatform === p.key;
                  return (
                    <div
                      id={"platform-" + p.key}
                      key={p.key}
                      className={
                        "group relative rounded-2xl border bg-card/80 backdrop-blur p-6 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/5 transition-all scroll-mt-28 " +
                        (isHighlighted
                          ? "border-primary ring-2 ring-primary/40"
                          : featured
                          ? "border-primary/50 ring-1 ring-primary/20 hover:border-primary/70"
                          : "border-border hover:border-primary/60")
                      }
                    >
                      {featured && (
                        <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
                          Start here if you run a studio
                        </span>
                      )}
                      <p className="text-[11px] uppercase tracking-wide text-primary font-semibold mb-1.5">
                        {p.role}
                      </p>
                      <div className="flex items-start gap-3 mb-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/15 group-hover:scale-105 transition-transform">
                          <Icon className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold leading-tight">
                            {p.title}
                          </h2>
                          <p className="text-xs text-muted-foreground">
                            {p.audience}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {p.oneLiner}
                      </p>
                      {p.subtext && (
                        <p className="text-xs text-muted-foreground/80 mb-3 italic">
                          {p.subtext}
                        </p>
                      )}
                      {/* Offer pill (item #10) */}
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-[11px] text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          {p.offer}
                        </span>
                      </div>
                      {/* What happens after signup (item #6) */}
                      <ul className="mb-5 space-y-1">
                        {p.afterSignup.map((step, idx) => (
                          <li key={step} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <span className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                              {idx + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-2">
                        <Button asChild size="sm" className="flex-1">
                          <Link
                            to={p.primaryCta.href}
                            onClick={() => onCardClick(p.key, "primary")}
                          >
                            {p.primaryCta.label}
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </Link>
                        </Button>
                        <Button asChild size="sm" variant="outline" className="flex-1">
                          <Link
                            to={p.secondaryCta.href}
                            onClick={() => onCardClick(p.key, "secondary")}
                          >
                            {p.secondaryCta.label}
                          </Link>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Item #19/20 — differentiation one-liners against point solutions */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-border bg-card/60 backdrop-blur px-3 py-2 flex items-start gap-2">
                  <Video className="h-3.5 w-3.5 text-primary mt-[2px] shrink-0" />
                  <span className="text-muted-foreground">
                    Self-tapes that <span className="font-medium text-foreground">count toward Readiness</span> — not just a recording app.
                  </span>
                </div>
                <div className="rounded-lg border border-border bg-card/60 backdrop-blur px-3 py-2 flex items-start gap-2">
                  <ClipboardList className="h-3.5 w-3.5 text-primary mt-[2px] shrink-0" />
                  <span className="text-muted-foreground">
                    Submissions <span className="font-medium text-foreground">backed by training history</span> — not just a headshot.
                  </span>
                </div>
              </div>

              {/* Trust strip */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                  COPPA &amp; FERPA compliant
                </span>
                <span aria-hidden>·</span>
                <span>Moderated youth spaces</span>
                <span aria-hidden>·</span>
                <span>No data sold</span>
                <span aria-hidden>·</span>
                <Link to="/trust-center" className="underline underline-offset-4 hover:text-foreground">
                  Trust Center
                </Link>
              </div>

              {/* Item #8 — expansion modules surfaced as visible chips, not a footnote */}
              <div className="mt-8">
                <p className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-3">
                  Also in the ecosystem
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {EXPANSION_MODULES.map((m) => {
                    const Icon = m.icon;
                    return (
                      <Link
                        key={m.key}
                        to="/platforms"
                        className="rounded-xl border border-border bg-card/60 backdrop-blur px-3 py-2.5 hover:border-primary/50 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                          <span className="text-sm font-medium">{m.label}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-snug">{m.blurb}</p>
                      </Link>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] text-center text-muted-foreground">
                  <Link to="/platforms" className="underline underline-offset-4 hover:text-foreground inline-flex items-center gap-1">
                    <Layers className="h-3 w-3" />
                    Browse the full ecosystem catalog
                  </Link>
                </p>
              </div>

              {/* Item #18 — name the three moats once, plainly */}
              <MoatsStrip />

              {/* Studio / agency pilot rail — moved below grid; still visible. */}
              <div className="mt-8 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] via-card/60 to-card/40 backdrop-blur p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground">
                      Running a studio, agency, or management roster?
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Book a 20-minute pilot call to see roster, assignments, and readiness in one place.
                    </div>
                  </div>
                </div>
                <Button asChild size="sm" className="gap-1.5 shrink-0">
                  <Link
                    to="/book-demo"
                    onClick={() => trackBoth("hub_launcher_click", { platform: "coach", kind: "pilot_call" })}
                  >
                    <Calendar className="h-4 w-4" />
                    Book a pilot call
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              {/* Item #16 — explicit parent CTA, beyond the CZ card subtext. */}
              <div className="mt-4 rounded-2xl border border-border bg-card/60 backdrop-blur p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Megaphone className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-sm font-medium">Parent of a young performer?</div>
                    <div className="text-xs text-muted-foreground">
                      Track practice, see tapes, and stay in the loop — COPPA-aligned by default.
                    </div>
                  </div>
                </div>
                <Button asChild size="sm" variant="outline">
                  <Link
                    to="/signup/callback-zone/plan"
                    onClick={() => trackBoth("hub_launcher_click", { platform: "callback-zone", kind: "parent_cta" })}
                  >
                    Start a parent trial
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="mt-4 rounded-2xl border border-border bg-card/60 backdrop-blur p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Gift className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-sm font-medium">Have a code?</div>
                    <div className="text-xs text-muted-foreground">
                      Redeem a beta, box, or partner code.
                    </div>
                  </div>
                </div>
                <Button asChild size="sm" variant="outline">
                  <Link to="/redeem">
                    Redeem code
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
                <Link
                  to="/demo"
                  className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
                >
                  Explore product demos
                </Link>
                <a
                  href={MAIN_SITE_LINKS.home}
                  className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors inline-flex items-center gap-1"
                >
                  Learn about CastReady
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a
                  href={MAIN_SITE_LINKS.pricing}
                  className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors inline-flex items-center gap-1"
                  title="Public pricing lives on castready.org. Already a customer? Upgrade in-app via Settings → Billing."
                >
                  Pricing
                  <ExternalLink className="h-3 w-3" />
                </a>
                <Link
                  to="/status"
                  className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
                >
                  Status
                </Link>
                <a href="https://castready.org/newsroom" target="_blank" rel="noopener" className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"> Changelog </a>
                <Link
                  to="/support"
                  className="px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
                >
                  Support
                </Link>
              </div>
            </div>
          </section>
          {/* Physical-digital loop: Training Kits → Callback Zone unlock */}
          <TrainingBoxesSection />
        </MainContent>
        <Footer />
      </div>
    </>
  );
};

export default HubLauncher;